import { Box, TableCell, Tooltip, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { DocumentObject, DocumentObjectProperties, S3File } from 'types';
import { DownloadButton } from 'components/Buttons/DownloadButton';
import { documentObjectsQuery } from 'dataLayer/Document/get';
import { useDocumentObjectMutation } from 'dataLayer/Document/mutations';
import { getDocumentTableStyles } from 'pages/Deals/Deal/documentsTableStyles';
import { filterAssociatedDocumentObjects } from 'helpers/utils';
import { FileAssociationsDropdownMenu } from 'components/DropdownMenus/AssociationsDropdownMenu';
import { updateDocumentProperties } from 'dataLayer/Document/api';

interface IProps {
  file: S3File;
  dealId: string;
}

export const CSCFileTableCells: FunctionComponent<IProps> = ({
  file,
  dealId,
}) => {
  const {
    dataAssociationColumnStyle,
    documentNameColumnStyle,
    uploadDateColumnStyle,
  } = getDocumentTableStyles('fileStyle');

  const { data: documentObjects, isLoading: isLoadingDocumentObjects } =
    useQuery(documentObjectsQuery(dealId));

  const customObjectAssociations = useMemo(
    () => filterAssociatedDocumentObjects(documentObjects, file.name),
    [documentObjects, file.name],
  );

  const [selectedDocuments, setSelectedDocuments] = useState<DocumentObject[]>(
    customObjectAssociations ?? [],
  );

  const updateDocumentObjectHandler = async (documentInfo: {
    documentIds: string[];
    documentProperties: Partial<DocumentObjectProperties>;
  }) => {
    await updateDocumentProperties(
      documentInfo.documentIds,
      documentInfo.documentProperties,
    );
  };

  const updateDocumentObjectMutation = useDocumentObjectMutation(
    updateDocumentObjectHandler,
    dealId,
  );

  const lastModifiedDate = file.lastModified
    ? format(parseISO(file.lastModified), 'P')
    : '';

  useEffect(() => {
    if (customObjectAssociations)
      setSelectedDocuments(customObjectAssociations);
  }, [customObjectAssociations]);

  return (
    <>
      {/* File Name and Download Button */}
      <TableCell sx={documentNameColumnStyle}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DownloadButton
            dealId={dealId}
            fileInformation={{
              fileName: file.name,
              fileType: 'documents',
            }}
          />
        </Box>
      </TableCell>

      {/* File Last Modified Date  */}
      <TableCell sx={uploadDateColumnStyle}>
        <Tooltip title={lastModifiedDate} enterDelay={0}>
          <Typography
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {lastModifiedDate}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Dropdown to associate file to HubSpot document(s)  */}
      {!isLoadingDocumentObjects && (
        <TableCell sx={dataAssociationColumnStyle}>
          <FileAssociationsDropdownMenu
            dealId={dealId}
            file={file}
            selectedDocuments={selectedDocuments}
            setSelectedDocuments={setSelectedDocuments}
            updateDocumentObjectMutation={updateDocumentObjectMutation}
          />
        </TableCell>
      )}
    </>
  );
};
